import { Box, useTheme } from '@mui/material'
import { GridColumns } from '@mui/x-data-grid-pro'
import { useCallback, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { toast } from 'react-toastify'
import { invalidationActivityLogCache } from 'src/api/activity-log'
import { invalidateCustomer } from 'src/api/customer'
import { invalidateDeviation } from 'src/api/deviation'
import { invalidateDocument } from 'src/api/document'
import { invalidateLocalityCache } from 'src/api/localities'
import { invalidateMainComponentCache, useUpdateMainComponentToOutgoing } from 'src/api/main-component'
import { invalidateStorageCache } from 'src/api/storage'
import { invalidateDocumentCategory } from 'src/api/users/document-category'
import { DynamicTextTypography } from 'src/components/DynamicTextTypography'
import GenericInformationTooltip from 'src/components/common/GenericInformationTootip'
import { GridActionButton } from 'src/components/common/GridActionButton'
import { EmptyStatusCard } from 'src/components/mooring/EmptyStatusCard'
import GenericTable from 'src/components/table/GenericTable'
import { CustomerStorageCtx } from 'src/context/StorageContext'
import { DialogContextType } from 'src/enums'
import { IMainComponent } from 'src/generated-types/main-component'
import { useDialogCtx, useStorageCtx } from 'src/hooks/context-hooks'
import i18n from 'src/i18n'
import { MUIThemeColors } from 'src/mui-theme'
import { getMainComponentIcon } from 'src/screens/localities/dashboard/mainComponent/MainComponentItem'
import { EquipmentDetailModal } from 'src/screens/localities/mainComponents/EquipmentDetailModal'
import { MainComponentSendModalComponent } from 'src/screens/localities/positions/components/MainComponentSendModal'
import { SendToLocalityDialog } from 'src/screens/serviceStations/SendToLocalityDialog'
import { Flex } from 'src/styles/flexComponent'
import { getEquipmentTooltipData, getThemeColor, getTranslatedText } from 'src/utils'
import { getCageName, getMooringName, validatePositionAndInstallation } from 'src/utils/function'
import NoEquipment from '../../assets/svg/no-items-common-image.svg'

interface StoredEquipmentTableProps {
  data: Array<IMainComponent> | undefined
  isLoading: boolean
  customerId: EntityId
  storageId: EntityId
}

const StoredEquipmentTable = ({ data, isLoading, customerId, storageId }: StoredEquipmentTableProps) => {
  const { t } = useTranslation(['storage', 'equipment', 'main-component'])
  const getTranslatedLabel = property =>
    ['en', 'us'].includes(i18n.language)
      ? JSON.parse(property.name as string).en
      : JSON.parse(property.name as string).no
  const theme = useTheme()
  const { t: equipmentTypeTranslation } = useTranslation('cage-status')
  const { showDialog } = useDialogCtx()
  const { permission } = useStorageCtx()
  const [selectedMainComponent, setSelectedMainComponent] = useState(0)
  const { mutate: updateMainComponentToOutgoing } = useUpdateMainComponentToOutgoing(
    customerId as EntityId,
    selectedMainComponent as EntityId,
  )

  const onClickEquipment = (mainComponent: IMainComponent) => {
    showDialog(
      EquipmentDetailModal,
      {
        componentProps: {
          customerId: customerId as EntityId,
          permission: permission,
          context: CustomerStorageCtx,
          mainComponentId: mainComponent.id as EntityId,
          mainComponentTypeId: mainComponent.type.id as EntityId,
          storageId: storageId,
        },
        opts: {
          maxWidth: false,
          fullWidth: true,
          bgColor: getThemeColor(theme, MUIThemeColors.backgroundMain),
          rootWidth: '1400px',
        },
        title: `${getTranslatedText(mainComponent?.type.name)} - ${mainComponent?.serialNumber} ${
          mainComponent.position
            ? ` / ${getMooringName(mainComponent.position.mooringName || '')} - ${getCageName(
                mainComponent.position.name,
                mainComponent.position.reference,
              )}`
            : ''
        }`,
      },
      DialogContextType.storageContext,
      true,
    )
  }

  const onSubmit = useCallback((form, pop, formContext?) => {
    if (!validatePositionAndInstallation(form, t, formContext)) {
      return
    }
    updateMainComponentToOutgoing(form, {
      onSuccess: () => {
        invalidateStorageCache.getEquipmentInStorage(customerId, storageId as EntityId)
        invalidateCustomer.getCustomersActivityCounts(customerId)
        invalidationActivityLogCache.getCageActivityLogs()
        invalidateDocumentCategory.getDocumentCategoriesWithDocuments(customerId)
        invalidateDocument.getDocuments(customerId)
        invalidateDocument.getDocumentsForApproval(customerId)
        invalidateLocalityCache.getCageStatus(customerId)
        invalidateLocalityCache.getLocality(customerId)
        invalidateMainComponentCache.getMainComponentById(customerId as EntityId, form.id as EntityId)
        invalidateDeviation.getDeviations(customerId)
        toast.success(t('sendToLocalitySuccess', { ns: 'main-component' }))
        pop()
      },
      onError: error => {
        if (error['data'] == 'CertificateNumberExists') {
          toast(t('certificateNumberExists', { ns: 'main-component' }), { type: 'error' })
          if (formContext && typeof formContext.setError === 'function') {
            formContext.setError('certificateNumber', {
              type: 'manual',
              message: t('certificateNumberExists', { ns: 'main-component' }),
            })
          }
        }
      },
    })
  }, [])

  const onSendToLocality = (mainComponentId, mainComponentTypeId) => {
    showDialog(
      SendToLocalityDialog,
      {
        componentProps: {
          customerId: customerId as EntityId,
          mainComponentId: mainComponentId as EntityId,
          mainComponentTypeId: mainComponentTypeId as EntityId,
          onSubmit: onSubmit,
        },
        opts: {
          maxWidth: 'sm',
          fullWidth: true,
        },
      },
      undefined,
      true,
    )
  }

  const handleSendToLocalityClick = (row: IMainComponent) => {
    setSelectedMainComponent(row.id)
    onSendToLocality(row.id, row.type.id as EntityId)
  }

  const sendToServiceStorage = async mainComponentId => {
    await showDialog(MainComponentSendModalComponent, {
      title: t('sendTo', { ns: 'main-component' }),
      componentProps: {
        customerId: customerId as EntityId,
        id: mainComponentId as EntityId,
        onlyServiceStation: false,
        storageId: storageId,
      },
      opts: {
        maxWidth: 'sm',
        fullWidth: true,
      },
    })
  }

  const hiddenItemsForTooltip = useMemo(() => ['description', 'status'], [])

  const EQUIPMENT_STATIC_COLUMNS: GridColumns = [
    {
      field: 'id',
      headerName: '',
      flex: 0.15,
      renderCell: ({ row }) =>
        getMainComponentIcon(
          row.type.componentType,
          equipmentTypeTranslation,
          getThemeColor(theme, MUIThemeColors.primaryMain),
        ),
    },
    {
      field: 'type',
      headerName: t('type'),
      flex: 1,
      renderCell: ({ row }) => (
        <DynamicTextTypography text={getTranslatedLabel(row.type)} variant="body1" maxLength={35} fontWeight={400} />
      ),
    },
    {
      field: 'description',
      headerName: t('description'),
      flex: 1,
      renderCell: ({ row }) => (
        <DynamicTextTypography text={row.description} variant="body1" maxLength={35} fontWeight={400} />
      ),
    },
    {
      field: 'serialNumber',
      headerName: t('product-number'),
      flex: 1,
      renderCell: ({ row }) => (
        <GenericInformationTooltip data={getEquipmentTooltipData(row as IMainComponent, hiddenItemsForTooltip)}>
          <DynamicTextTypography
            text={row.serialNumber}
            variant="body1"
            maxLength={35}
            fontWeight={500}
            isTooltipDisabled
          />
        </GenericInformationTooltip>
      ),
    },
  ]

  const columns: GridColumns = useMemo(
    () => [
      ...EQUIPMENT_STATIC_COLUMNS,
      ...[
        {
          field: 'actions',
          type: 'actions',
          flex: 2,
          renderCell: params => [
            <Flex.Row gap={1} key={'actions'} justifyContent={'right'} alignItems={'end'} width={'100%'} padding={1}>
              <GridActionButton
                key={'send-to-service-or-storage'}
                buttonText={t('send-service-or-storage')}
                variant="outlined"
                shadowWeight={0}
                onClick={() => {
                  sendToServiceStorage(params.row.id)
                }}
                buttonTextProps={{
                  color: getThemeColor(theme, MUIThemeColors.black),
                  fontWeight: 500,
                }}
              />
              <GridActionButton
                key={'send-to-locality'}
                variant="outlined"
                shadowWeight={0}
                onClick={() => {
                  handleSendToLocalityClick(params.row)
                }}
                buttonText={t('send-to-locality')}
                buttonTextProps={{
                  color: getThemeColor(theme, MUIThemeColors.black),
                  fontWeight: 500,
                }}
              />
            </Flex.Row>,
          ],
        },
      ],
    ],
    [t, sendToServiceStorage, setSelectedMainComponent, onSendToLocality, hiddenItemsForTooltip],
  )

  return (
    <div>
      <GenericTable
        id="stored_equipment"
        columns={columns}
        data={data}
        loading={isLoading}
        autoHeight={false}
        height={window.innerHeight - 300}
        onRowClick={({ row }) => onClickEquipment(row as IMainComponent)}
        noDataComponent={() =>
          data?.length == 0 ? (
            <Box height="100%">
              <EmptyStatusCard
                imageWidth="165px"
                image={NoEquipment}
                header={t('equipment-table-empty-status-title', { ns: 'equipment' })}
                content={t('equipment-table-empty-status-content', { ns: 'storage' })}
                headerFontSize="18px"
                contentFontSize="14px"
                contentClasses="mt-2"
                contentTopMargin="10px"
                imageContainerClasses="pb-4 pt-4"
                height="100%"
              />
            </Box>
          ) : null
        }
      />
    </div>
  )
}

export default StoredEquipmentTable
